import React from 'react'
import { DateTime } from 'luxon'
import Box from '@material-ui/core/Box'
import Typography from '@material-ui/core/Typography'
import Button from '@material-ui/core/Button'
import capitalize from 'lodash/capitalize'
import LaunchOutlinedIcon from 'components/material-symbol/icons/Launch'
import ReceiptIcon from 'components/material-symbol/icons/Receipt'
import CalendarTodayIcon from 'components/material-symbol/icons/CalendarToday'
import AddToPhotosIcon from 'components/material-symbol/icons/AddToPhotos'
import PersonIcon from 'components/material-symbol/icons/Person'
import CreditCardIcon from 'components/material-symbol/icons/CreditCard'
import CheckIcon from 'components/material-symbol/icons/Check'
import ScheduleIcon from 'components/material-symbol/icons/Schedule'
import { useAuth } from 'lib/auth'
import { safeFormat } from 'lib/family-cup-utils/event-dates'
import {
  getProductName,
  getTransactionType,
  groupLineItems,
  sumField,
} from 'components/transaction-summary/transaction-utils'
import { useStyles } from './styles'

const monthDayYearFormat = 'MM/dd/yyyy'

const formatCurrency = amountInCents =>
  `${amountInCents < 0 ? '-$' : '$'}${(Math.abs(amountInCents) / 100).toFixed(
    2,
  )}`

const byCreatedAt = (a, b) =>
  DateTime.fromISO(a.createdAt) > DateTime.fromISO(b.createdAt) ? 1 : -1

const byCreatedAtThenLineType = (a, b) => {
  if (a.createdAt === b.createdAt) {
    return a.lineType > b.lineType ? 1 : -1
  }
  return byCreatedAt(a, b)
}

const lineItemLabel = ({
  createdAt,
  discountCode,
  label,
  lineType,
  refund,
}) => {
  if (refund) {
    return `Refund (${safeFormat(createdAt, monthDayYearFormat)})`
  }
  if (lineType === 'DISCOUNT') {
    return `${label} (${discountCode})`
  }
  return label
}

const SummaryCard = ({ title, children, headerAction, classes }) => (
  <Box className={classes.card}>
    <Box className={classes.cardHeader}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        gap={2}
      >
        <Typography className={classes.cardTitle}>{title}</Typography>
        {headerAction}
      </Box>
    </Box>
    <Box className={classes.cardDivider} />
    <Box className={classes.cardContent}>{children}</Box>
  </Box>
)

const SummaryField = ({ label, icon: Icon, children, classes }) => (
  <Box className={classes.summaryField}>
    <Typography className={classes.summaryLabel}>{label}</Typography>
    <Box className={classes.summaryValueRow}>
      <Box className={classes.summaryIconWrap}>
        <Icon
          className={classes.summaryIcon}
          fontSize="small"
          opticalSize={20}
        />
      </Box>
      <Box minWidth={0}>{children}</Box>
    </Box>
  </Box>
)

const FeeLineItemsSection = ({ transaction, classes }) => {
  const { lineItems, subtotal } = transaction

  return (
    <Box className={classes.feeSection}>
      {lineItems.sort(byCreatedAtThenLineType).map((lineItem, index) => {
        const { amountInCents } = lineItem
        const displayLabel = lineItemLabel(lineItem)

        return (
          <Box key={`${displayLabel}-${index}`} className={classes.feeRow}>
            <Typography component="span">{displayLabel}</Typography>
            <Typography component="span">
              {formatCurrency(amountInCents)}
            </Typography>
          </Box>
        )
      })}
      <Box className={`${classes.feeRow} ${classes.feeRowBold}`}>
        <Typography component="span">Subtotal</Typography>
        <Typography component="span">{formatCurrency(subtotal)}</Typography>
      </Box>
    </Box>
  )
}

const OrderTotalSection = ({ total, lineItems, classes }) => {
  const coachingFees = lineItems.find(item => item.group === 'COACHING_FEES')
  const offlineCoachingFee =
    coachingFees?.lineItems.find(
      item => item.label === 'Coaching Fee (Offline)',
    )?.amountInCents * -1 || 0

  return (
    <>
      <Box className={`${classes.feeRow} ${classes.feeRowBold}`}>
        <Typography component="span">Order Total</Typography>
        <Typography component="span">{formatCurrency(total)}</Typography>
      </Box>
      <Box className={`${classes.feeRow} ${classes.feeRowBold}`}>
        <Typography component="span">Offline Fees</Typography>
        <Typography component="span">
          {formatCurrency(offlineCoachingFee)}
        </Typography>
      </Box>
      <Box className={`${classes.feeRow} ${classes.feeRowBold}`}>
        <Typography component="span">Consumer Total</Typography>
        <Typography component="span">
          {formatCurrency(total + offlineCoachingFee)}
        </Typography>
      </Box>
    </>
  )
}

const TransactionRecordRow = ({ transaction, classes }) => {
  const { amountInCents, createdAt, refund, refundReason } = transaction
  const date = safeFormat(createdAt, monthDayYearFormat)
  const type = refund ? 'Refund' : 'Original Purchase'

  return (
    <Box className={classes.recordRow}>
      <Box minWidth={0}>
        <Box className={classes.recordRowPrimary}>
          <Typography className={classes.recordDate}>{date}</Typography>
          <Typography className={classes.recordAmount}>
            {formatCurrency(amountInCents)}
          </Typography>
        </Box>
        {refund && refundReason ? (
          <Typography className={classes.recordRefundReason}>
            {refundReason}
          </Typography>
        ) : null}
      </Box>
      <Typography className={classes.recordType}>{type}</Typography>
    </Box>
  )
}

const AccountTransactionSummary = ({
  registrationPayment,
  paymentSourceType,
}) => {
  const classes = useStyles()
  const { isCoach } = useAuth()

  const {
    payor,
    transactions,
    products,
    registrationPaymentCharge,
    ...payment
  } = registrationPayment

  const paymentSourceTypes = paymentSourceType.enumValues
  const payorName = payor.name || `${payor.firstName} ${payor.lastName}`
  const groupedLineItems = groupLineItems(transactions)
  const orderTotal = groupedLineItems.reduce(sumField('subtotal'), 0)
  const lastUpdated = safeFormat(payment.updatedAt, monthDayYearFormat)
  const initialTransaction = [...transactions].sort(byCreatedAt)[0]
  const product = getProductName(products[0])
  const transactionType = getTransactionType(products[0])

  const paymentMethod = (() => {
    if (payment.cardBrand && payment.last4) {
      return `${payment.cardBrand.toUpperCase()} ending in ${payment.last4}`
    }

    const source = paymentSourceTypes.find(
      sourceType => sourceType.name === initialTransaction.sourceType,
    )?.description

    return `${source} (collected offline)`
  })()

  return (
    <Box className={classes.stack}>
      <SummaryCard title="Transaction Summary" classes={classes}>
        <Box className={classes.summaryGrid}>
          <SummaryField
            label="Transaction Number"
            icon={ReceiptIcon}
            classes={classes}
          >
            <Typography className={classes.summaryValue}>
              {payment.id}
            </Typography>
          </SummaryField>
          <SummaryField
            label="Billing Information"
            icon={PersonIcon}
            classes={classes}
          >
            <Typography className={classes.summaryValue}>
              {payorName}
            </Typography>
            <Typography className={classes.summarySecondary}>
              {payor.email}
            </Typography>
          </SummaryField>
          <SummaryField
            label="Date of Purchase"
            icon={CalendarTodayIcon}
            classes={classes}
          >
            <Typography className={classes.summaryValue}>
              {safeFormat(payment.createdAt, monthDayYearFormat)}
            </Typography>
          </SummaryField>
          <SummaryField
            label="Payment Method"
            icon={CreditCardIcon}
            classes={classes}
          >
            <Typography className={classes.summaryValue}>
              {paymentMethod}
            </Typography>
            {registrationPaymentCharge ? (
              <>
                <Typography className={classes.summarySecondary}>
                  {`${capitalize(
                    registrationPaymentCharge.chargeType,
                  )} Number: ${registrationPaymentCharge.chargeNumber}`}
                </Typography>
                {registrationPaymentCharge.checkNumber ? (
                  <Typography className={classes.summarySecondary}>
                    {`Check Number: ${registrationPaymentCharge.checkNumber}`}
                  </Typography>
                ) : null}
              </>
            ) : null}
          </SummaryField>
          <SummaryField
            label="Product"
            icon={AddToPhotosIcon}
            classes={classes}
          >
            <Typography className={classes.summaryValue}>{product}</Typography>
          </SummaryField>
          <SummaryField
            label="Transaction Type"
            icon={CheckIcon}
            classes={classes}
          >
            <Typography className={classes.summaryValue}>
              {transactionType}
            </Typography>
          </SummaryField>
        </Box>
        <Box className={classes.cardDivider} />
        <Box className={classes.lastUpdatedRow}>
          <ScheduleIcon className={classes.lastUpdatedIcon} fontSize="small" />
          <span>Last Updated: {lastUpdated}</span>
        </Box>
      </SummaryCard>

      {isCoach
        ? groupedLineItems.map((item, index) => (
          <SummaryCard
            key={`${item.title}-${index}`}
            title={item.title}
            classes={classes}
          >
            <Box className={classes.feesBody}>
              <FeeLineItemsSection transaction={item} classes={classes} />
              {index === groupedLineItems.length - 1 ? (
                <OrderTotalSection
                  total={orderTotal}
                  lineItems={groupedLineItems}
                  classes={classes}
                />
              ) : null}
            </Box>
          </SummaryCard>
        ))
        : null}

      <SummaryCard
        title="Transaction Record"
        classes={classes}
        headerAction={
          initialTransaction?.stripeReceiptUrl ? (
            <Button
              component="a"
              href={initialTransaction.stripeReceiptUrl}
              target="_blank"
              rel="noopener"
              className={classes.receiptLink}
              startIcon={
                <LaunchOutlinedIcon fontSize="small" opticalSize={18} />
              }
            >
              View Stripe Receipt
            </Button>
          ) : null
        }
      >
        <Box className={classes.recordStack}>
          {Array.from(transactions)
            .sort(byCreatedAt)
            .map((transaction, index) => (
              <TransactionRecordRow
                key={`${transaction.createdAt}-${index}`}
                transaction={transaction}
                classes={classes}
              />
            ))}
        </Box>
      </SummaryCard>
    </Box>
  )
}

export default AccountTransactionSummary
